import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import schedulingApi from '../../api/scheduling';
import useHarnessEnabled from '../../hooks/useHarnessEnabled';
import SimulateAvailabilityModal from './SimulateAvailabilityModal';

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  DRAFT:      'bg-gray-100 text-gray-600',
  OPEN:       'bg-green-100 text-green-700',
  CLOSED:     'bg-yellow-100 text-yellow-700',
  GENERATING: 'bg-blue-100 text-blue-700',
  GENERATED:  'bg-indigo-100 text-indigo-700',
  PUBLISHED:  'bg-purple-100 text-purple-700',
};

function StatusBadge({ status }) {
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

// ── Create period form ────────────────────────────────────────────────────────

function CreatePeriodForm({ semesters, onCreated }) {
  const [form, setForm] = useState({
    semester: '',
    start_date: '',
    end_date: '',
    max_no_weekdays: '',
    max_no_weekends: '',
    availability_deadline: '',
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: schedulingApi.createPeriod,
    onSuccess: (data) => { onCreated(data); setError(''); },
    onError: (err) => setError(err.data ? JSON.stringify(err.data) : err.message),
  });

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      max_no_weekdays: parseInt(form.max_no_weekdays, 10),
      max_no_weekends: parseInt(form.max_no_weekends, 10),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
      <h3 className="font-semibold text-gray-800">Create New Period</h3>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Semester" required>
          <select value={form.semester} onChange={set('semester')} required className={inputCls}>
            <option value="">— Select —</option>
            {semesters.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Availability Deadline" required>
          <input type="datetime-local" value={form.availability_deadline}
            onChange={set('availability_deadline')} required className={inputCls} />
        </Field>

        <Field label="Start Date" required>
          <input type="date" value={form.start_date}
            onChange={set('start_date')} required className={inputCls} />
        </Field>

        <Field label="End Date" required>
          <input type="date" value={form.end_date}
            onChange={set('end_date')} required className={inputCls} />
        </Field>

        <Field label={`Max "No" — Weekdays`} required>
          <input type="number" min="0" value={form.max_no_weekdays}
            onChange={set('max_no_weekdays')} required className={inputCls} />
        </Field>

        <Field label={`Max "No" — Weekends`} required>
          <input type="number" min="0" value={form.max_no_weekends}
            onChange={set('max_no_weekends')} required className={inputCls} />
        </Field>
      </div>

      <button
        type="submit"
        disabled={mutation.isPending}
        className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
      >
        {mutation.isPending ? 'Creating…' : 'Create Period'}
      </button>
    </form>
  );
}

// ── Heavy day manager ─────────────────────────────────────────────────────────

function HeavyDayManager({ period }) {
  const queryClient = useQueryClient();
  const [newDate, setNewDate] = useState('');

  const { data: heavyDays = [] } = useQuery({
    queryKey: ['heavy-days', period.id],
    queryFn:  () => schedulingApi.getHeavyDays(period.id),
  });

  const addMutation = useMutation({
    mutationFn: (date) => schedulingApi.createHeavyDay({ period: period.id, date }),
    onSuccess: () => {
      setNewDate('');
      queryClient.invalidateQueries({ queryKey: ['heavy-days', period.id] });
      queryClient.invalidateQueries({ queryKey: ['periods'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: schedulingApi.deleteHeavyDay,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['heavy-days', period.id] });
      queryClient.invalidateQueries({ queryKey: ['periods'] });
    },
  });

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Heavy Days</p>
      <div className="flex flex-wrap gap-2">
        {heavyDays.map((h) => (
          <span key={h.id}
            className="flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 px-2.5 py-0.5 text-xs font-medium">
            {h.date}
            {period.status === 'DRAFT' && (
              <button
                onClick={() => removeMutation.mutate(h.id)}
                className="ml-1 text-orange-400 hover:text-orange-700"
                title="Remove"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {!heavyDays.length && <span className="text-xs text-gray-400">None set</span>}
      </div>

      {/* Add heavy day — only while DRAFT */}
      {period.status === 'DRAFT' && (
        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={newDate}
            min={period.start_date}
            max={period.end_date}
            onChange={(e) => setNewDate(e.target.value)}
            className={inputCls + ' w-40'}
          />
          <button
            onClick={() => newDate && addMutation.mutate(newDate)}
            disabled={!newDate || addMutation.isPending}
            className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600 disabled:opacity-40"
          >
            + Add Heavy Day
          </button>
        </div>
      )}
    </div>
  );
}

// ── Period row ────────────────────────────────────────────────────────────────

function PeriodRow({ period }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [simOpen,  setSimOpen]  = useState(false);
  const harnessEnabled = useHarnessEnabled();

  const actionMutation = useMutation({
    mutationFn: ({ action, id }) => {
      if (action === 'open')     return schedulingApi.openPeriod(id);
      if (action === 'close')    return schedulingApi.closePeriod(id);
      if (action === 'generate') return schedulingApi.generateSchedule(id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['periods'] }),
  });

  const act = (action) => actionMutation.mutate({ action, id: period.id });
  const missingFields = [
    period.max_no_weekdays == null ? 'weekday max' : null,
    period.max_no_weekends == null ? 'weekend max' : null,
    !period.availability_deadline ? 'deadline' : null,
  ].filter(Boolean);

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      {/* Summary row */}
      <div
        className="flex flex-wrap items-center gap-3 p-4 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="font-medium text-gray-900 text-sm">
          {period.start_date} → {period.end_date}
        </span>
        <StatusBadge status={period.status} />

        {!period.is_ready && period.status === 'DRAFT' && (
          <span className="text-xs text-red-500">
            Missing: {missingFields.join(', ') || 'required fields'}
          </span>
        )}

        <div className="ml-auto flex gap-2">
          {period.status === 'DRAFT' && (
            <ActionButton
              label="Open Availability"
              disabled={!period.is_ready || actionMutation.isPending}
              onClick={(e) => { e.stopPropagation(); act('open'); }}
              color="green"
            />
          )}
          {period.status === 'OPEN' && (
            <>
              {harnessEnabled && (
                <ActionButton
                  label="Simulate Availability"
                  disabled={actionMutation.isPending}
                  onClick={(e) => { e.stopPropagation(); setSimOpen(true); }}
                  color="gray"
                />
              )}
              <ActionButton
                label="Close Availability"
                disabled={actionMutation.isPending}
                onClick={(e) => { e.stopPropagation(); act('close'); }}
                color="yellow"
              />
            </>
          )}
          {period.status === 'CLOSED' && (
            <ActionButton
              label="Generate Schedule"
              disabled={actionMutation.isPending}
              onClick={(e) => { e.stopPropagation(); act('generate'); }}
              color="indigo"
            />
          )}
          {period.status === 'GENERATING' && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-blue-600">
              <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Generating…
            </span>
          )}
        </div>

        <span className="text-gray-400 text-xs">{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3 text-sm text-gray-600 space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Detail label='Max "No" Weekdays' value={period.max_no_weekdays ?? '—'} />
            <Detail label='Max "No" Weekends' value={period.max_no_weekends ?? '—'} />
            <Detail label="Deadline" value={
              period.availability_deadline
                ? new Date(period.availability_deadline).toLocaleString()
                : '—'
            } />
          </div>
          <HeavyDayManager period={period} />
        </div>
      )}

      {simOpen && (
        <SimulateAvailabilityModal
          period={period}
          onClose={() => setSimOpen(false)}
          onDone={() => queryClient.invalidateQueries({ queryKey: ['periods'] })}
        />
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function PeriodsPanel() {
  const queryClient  = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [toast,      setToast]      = useState(null);
  const generatingRef = useRef(new Set());

  const flash = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const { data: semesters = [] } = useQuery({
    queryKey: ['semesters'],
    queryFn:  schedulingApi.getSemesters,
  });

  const { data: periods = [], isLoading } = useQuery({
    queryKey: ['periods'],
    queryFn:  schedulingApi.getPeriods,
    // Auto-poll every 3 s while any period is generating
    refetchInterval: (query) => {
      const data = query.state.data;
      return Array.isArray(data) && data.some(p => p.status === 'GENERATING')
        ? 3000
        : false;
    },
  });

  // Detect GENERATING → terminal status transitions and surface feedback
  useEffect(() => {
    const prev = generatingRef.current;
    const currentGenerating = new Set(
      periods.filter(p => p.status === 'GENERATING').map(p => p.id)
    );

    for (const id of prev) {
      if (!currentGenerating.has(id)) {
        const period = periods.find(p => p.id === id);
        if (!period) continue;
        if (period.status === 'GENERATED') {
          flash(`Schedule generated for ${period.start_date} → ${period.end_date}.`);
        } else if (period.status === 'CLOSED') {
          flash(
            `Generation failed for ${period.start_date} → ${period.end_date}. ` +
            `Check availability data and do-not-pair rules.`,
            'error'
          );
        }
      }
    }

    generatingRef.current = currentGenerating;
  }, [periods]);

  const sorted = [...periods].sort(
    (a, b) => new Date(b.start_date) - new Date(a.start_date)
  );

  return (
    <div className="space-y-5">
      {/* Generation status toast */}
      {toast && (
        <div className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm ${
          toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-4 opacity-50 hover:opacity-100">✕</button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Scheduling Periods</h2>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
        >
          {showCreate ? 'Cancel' : '+ New Period'}
        </button>
      </div>

      {showCreate && (
        <CreatePeriodForm
          semesters={semesters}
          onCreated={() => {
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ['periods'] });
          }}
        />
      )}

      {isLoading && (
        <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
      )}

      <div className="space-y-3">
        {sorted.map((p) => <PeriodRow key={p.id} period={p} />)}
        {!isLoading && !sorted.length && (
          <div className="rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center text-gray-400 text-sm">
            No periods yet. Create one to get started.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tiny shared components ────────────────────────────────────────────────────

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-gray-400">{label}</p>
      <p className="font-medium text-gray-800">{value}</p>
    </div>
  );
}

function ActionButton({ label, onClick, disabled, color }) {
  const colors = {
    green:  'bg-green-600 hover:bg-green-700',
    yellow: 'bg-yellow-500 hover:bg-yellow-600',
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    gray:   'bg-gray-500 hover:bg-gray-600',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40 transition-colors ${colors[color]}`}
    >
      {label}
    </button>
  );
}
